/**
 * Persistence: IndexedDB in the browser (via `idb`), memory everywhere else.
 *
 * The store talks to this narrow key/value interface rather than to `idb` directly, so the
 * same store code runs in node tests, and a browser that refuses IndexedDB (private mode, a
 * locked-down webview, a full disk) degrades to an in-memory session instead of a white
 * screen. `kind` says which one is live, so the shell can warn that nothing will survive.
 */
import type { IDBPDatabase } from 'idb'
import { openDB } from 'idb'

/**
 * trips         Trip, by id
 * foods         user-added or user-edited Food (overlays the bundled one with the same id)
 * recipes       user Recipe (same overlay rule)
 * availability  UserRating, by `category|food|region`
 * favourites    Favourite (a starred food or recipe), by `food:<id>` / `recipe:<id>`
 * meta          singletons: settings, sync, scratch basket, community ratings
 */
export type Table = 'trips' | 'foods' | 'recipes' | 'availability' | 'favourites' | 'meta'

export const TABLES: readonly Table[] = [
  'trips',
  'foods',
  'recipes',
  'availability',
  'favourites',
  'meta',
]

export interface StoreBackend {
  readonly kind: 'idb' | 'memory'
  getAll<T>(table: Table): Promise<T[]>
  /** Every row with its key. Meta rows are keyed by name, not by a field of the value. */
  entries<T>(table: Table): Promise<[string, T][]>
  get<T>(table: Table, key: string): Promise<T | undefined>
  put(table: Table, key: string, value: unknown): Promise<void>
  delete(table: Table, key: string): Promise<void>
  clear(table: Table): Promise<void>
}

function clone<T>(value: T): T {
  if (typeof value !== 'object' || value === null) return value
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : (JSON.parse(JSON.stringify(value)) as T)
}

export function memoryBackend(): StoreBackend {
  const tables = new Map<Table, Map<string, unknown>>()
  const of = (t: Table): Map<string, unknown> => {
    let m = tables.get(t)
    if (!m) tables.set(t, (m = new Map()))
    return m
  }
  // Cloned on the way in and out, like IndexedDB, so nobody mutates a stored record by accident.
  return {
    kind: 'memory',
    async getAll<T>(t: Table) {
      return [...of(t).values()].map((v) => clone(v) as T)
    },
    async entries<T>(t: Table) {
      return [...of(t).entries()].map(([k, v]) => [k, clone(v) as T] as [string, T])
    },
    async get<T>(t: Table, key: string) {
      const v = of(t).get(key)
      return v === undefined ? undefined : (clone(v) as T)
    },
    async put(t: Table, key: string, value: unknown) {
      of(t).set(key, clone(value))
    },
    async delete(t: Table, key: string) {
      of(t).delete(key)
    },
    async clear(t: Table) {
      of(t).clear()
    },
  }
}

export const DB_NAME = 'resupport'
/** 2 added `favourites`; the upgrade creates whatever store is missing. */
const DB_VERSION = 2

/**
 * IndexedDB, opened lazily. If opening fails, the backend becomes a memory backend for the
 * session and `kind` reports 'memory' from then on.
 */
export function idbBackend(name = DB_NAME): StoreBackend {
  let dbPromise: Promise<IDBPDatabase | null> | null = null
  let fellBack = false
  const fallback = memoryBackend()

  function db(): Promise<IDBPDatabase | null> {
    if (!dbPromise) {
      dbPromise = Promise.resolve()
        .then(() =>
          openDB(name, DB_VERSION, {
            upgrade(database) {
              for (const t of TABLES) {
                if (!database.objectStoreNames.contains(t)) database.createObjectStore(t)
              }
            },
          }),
        )
        .catch((err: unknown) => {
          console.warn('[resupport] IndexedDB unavailable, running in memory', err)
          fellBack = true
          return null
        })
    }
    return dbPromise
  }

  return {
    get kind() {
      return fellBack ? 'memory' : 'idb'
    },
    async getAll<T>(t: Table) {
      const d = await db()
      if (!d) return fallback.getAll<T>(t)
      return (await d.getAll(t)) as T[]
    },
    async entries<T>(t: Table) {
      const d = await db()
      if (!d) return fallback.entries<T>(t)
      const tx = d.transaction(t)
      const [keys, values] = await Promise.all([tx.store.getAllKeys(), tx.store.getAll()])
      return keys.map((k, i) => [String(k), values[i] as T] as [string, T])
    },
    async get<T>(t: Table, key: string) {
      const d = await db()
      if (!d) return fallback.get<T>(t, key)
      return (await d.get(t, key)) as T | undefined
    },
    async put(t: Table, key: string, value: unknown) {
      const d = await db()
      if (!d) return fallback.put(t, key, value)
      await d.put(t, value, key)
    },
    async delete(t: Table, key: string) {
      const d = await db()
      if (!d) return fallback.delete(t, key)
      await d.delete(t, key)
    },
    async clear(t: Table) {
      const d = await db()
      if (!d) return fallback.clear(t)
      await d.clear(t)
    },
  }
}

/** IndexedDB when the platform has it, memory otherwise. */
export function defaultBackend(): StoreBackend {
  return typeof indexedDB !== 'undefined' ? idbBackend() : memoryBackend()
}
